import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { FormatLocale } from '@/services/tenantConfig.service';
import { useFeatureFlag, FeatureFlag } from '@/services/featureFlag.service';
import { useDashboards } from '@/hooks/useDashboards';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import CustomDomainCard from './CustomDomainCard';
import OidcSsoCard from './OidcSsoCard';

// Format locale presets
const FORMAT_LOCALE_OPTIONS: { label: string; value: FormatLocale }[] = [
  {
    label: 'US (1,234.56)',
    value: {
      decimal: '.',
      thousands: ',',
      grouping: [3],
      currency: ['$', ''],
    },
  },
  {
    label: 'Europe (1.234,56)',
    value: {
      decimal: ',',
      thousands: '.',
      grouping: [3],
      currency: ['', '€'],
    },
  },
];

export default function GeneralTab() {
  const queryClient = useQueryClient();
  const [tenantConfigId, setTenantConfigId] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [formatLocale, setFormatLocale] = useState<FormatLocale>({
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: ['$', ''],
  });
  const [saving, setSaving] = useState(false);
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [defaultDashboardId, setDefaultDashboardId] = useState<string | null>(null);
  const showCustomDomain = useFeatureFlag(FeatureFlag.CUSTOM_DOMAIN);
  const { data: tenantConfig } = useTenantConfig();
  const { data: dashboardsData } = useDashboards({ pageSize: 500 });
  const dashboards = useMemo(() => dashboardsData?.records || [], [dashboardsData]);

  // Fetch tenant registration data
  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Initialize form with fetched data
  useEffect(() => {
    if (registrationData) {
      const { tenant_config, tenantRegistration } = registrationData;
      setTenantConfigId(tenant_config.id);
      setTenantSlug(tenant_config.tenant_slug || '');
      setTenantName(tenantRegistration?.name || tenant_config.tenant_name || '');
      setContactEmail(tenantRegistration?.contact_email || '');

      if (tenant_config.format_locale) {
        setFormatLocale(tenant_config.format_locale);
      }
    }
  }, [registrationData]);

  // Initialize default dashboard from tenant config
  useEffect(() => {
    if (tenantConfig?.default_dashboard_id !== undefined) {
      setDefaultDashboardId(tenantConfig.default_dashboard_id || null);
    }
  }, [tenantConfig]);

  const getFormatLocaleLabel = (locale: FormatLocale): string => {
    const match = FORMAT_LOCALE_OPTIONS.find(
      (opt) => JSON.stringify(opt.value) === JSON.stringify(locale)
    );
    return match?.label || 'Custom';
  };

  const saveContactEmail = useCallback(async () => {
    if (!contactEmail.trim() || !contactEmail.includes('@')) return;

    setSavingContact(true);
    try {
      await TenantConfigService.getInstance().updateContactEmail(contactEmail.trim());
      showToast.success('Contact email saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to save contact email', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingContact(false);
    }
  }, [contactEmail, queryClient]);

  const saveSettings = useCallback(async () => {
    if (!tenantConfigId) return;

    setSaving(true);
    try {
      await TenantConfigService.getInstance().saveFormatLocale(
        tenantConfigId,
        formatLocale
      );
      showToast.success('Settings saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, formatLocale, queryClient]);

  const saveDefaultDashboard = useCallback(async () => {
    if (!tenantConfigId) return;

    setSavingDashboard(true);
    try {
      await TenantConfigService.getInstance().saveDefaultDashboard(
        tenantConfigId,
        defaultDashboardId
      );
      showToast.success('Default dashboard saved');
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save default dashboard', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingDashboard(false);
    }
  }, [tenantConfigId, defaultDashboardId, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            Basic information about your Pliable account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Account Name</Label>
            <Input value={tenantName} disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label>Account ID</Label>
            <Input value={tenantSlug} disabled className="bg-muted font-mono" />
            <p className="text-sm text-muted-foreground">
              This identifier cannot be changed after creation
            </p>
          </div>

        </CardContent>
      </Card>

      {/* Formatting Card */}
      <Card>
        <CardHeader>
          <CardTitle>Formatting</CardTitle>
          <CardDescription>
            Configure how numbers and currency values are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Number Format</Label>
            <Select
              value={getFormatLocaleLabel(formatLocale)}
              onValueChange={(label) => {
                const option = FORMAT_LOCALE_OPTIONS.find(
                  (opt) => opt.label === label
                );
                if (option) {
                  setFormatLocale(option.value);
                }
              }}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_LOCALE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.label} value={opt.label}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Controls how numbers are formatted throughout the application
            </p>
          </div>

          <Button onClick={saveSettings} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Contact Email Card */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Email</CardTitle>
          <CardDescription>
            Primary contact email for this account. Used for notifications and alerts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              id="contact-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="admin@example.com"
            />
            <Button
              onClick={saveContactEmail}
              disabled={savingContact || !contactEmail.trim() || !contactEmail.includes('@')}
              className="shrink-0"
            >
              {savingContact ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Default Dashboard Card */}
      <Card>
        <CardHeader>
          <CardTitle>Default Dashboard</CardTitle>
          <CardDescription>
            Choose a dashboard to display on the home page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Dashboard</Label>
            <Select
              value={defaultDashboardId || '__none__'}
              onValueChange={(value) => {
                setDefaultDashboardId(value === '__none__' ? null : value);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="No dashboard selected" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {dashboards.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name || 'Untitled Dashboard'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              When set, this dashboard will be shown on the home page instead of the default welcome screen
            </p>
          </div>

          <Button onClick={saveDefaultDashboard} disabled={savingDashboard || defaultDashboardId === (tenantConfig?.default_dashboard_id || null)}>
            {savingDashboard ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Custom Domain Card */}
      {showCustomDomain && tenantConfigId && (
        <CustomDomainCard
          tenantConfigId={tenantConfigId}
          initialDomain={registrationData?.tenant_config.custom_domain || ''}
          initialCert={registrationData?.tenant_config.custom_domain_cert || null}
        />
      )}

      {/* OIDC SSO Card */}
      {tenantConfigId && (
        <OidcSsoCard
          tenantConfigId={tenantConfigId}
          customDomain={registrationData?.tenant_config.custom_domain || ''}
        />
      )}
    </div>
  );
}
